// Login.tsx

import React, { useState, useEffect, memo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged, signInWithCustomToken, User } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import app from '../../Firebase';

// ── OTP Form — Step 1 ────────────────────────────────────────────
const EmailForm = memo(({
  email,
  onEmailChange,
  onSubmit,
  loading,
}: {
  email: string;
  onEmailChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
}) => (
  <form onSubmit={onSubmit} style={s.form}>
    <div style={s.fieldGroup}>
      <label style={s.label}>Email</label>
      <input
        className="dp-input"
        type="email"
        placeholder="you@example.com"
        value={email}
        required
        autoComplete="email"
        autoFocus
        onChange={(e) => onEmailChange(e.target.value)}
      />
    </div>
    <button type="submit" className="dp-btn dp-btn-primary" disabled={loading}>
      {loading ? "Sending…" : "Send Code"}
    </button>
  </form>
));

// ── OTP Form — Step 2 ────────────────────────────────────────────
const OtpForm = memo(({
  email,
  otp,
  onOtpChange,
  onSubmit,
  onBack,
  onResend,
  loading,
  resendCooldown,
}: {
  email: string;
  otp: string[];
  onOtpChange: (index: number, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  onResend: () => void;
  loading: boolean;
  resendCooldown: number;
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const formRef = useRef<HTMLFormElement | null>(null);

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6).split("");
      digits.forEach((d, i) => onOtpChange(i, d));
      inputRefs.current[Math.min(digits.length - 1, 5)]?.focus();
      if (digits.length === 6) {
        setTimeout(() => formRef.current?.requestSubmit(), 50);
      }
      return;
    }
    const digit = value.replace(/\D/g, "");
    onOtpChange(index, digit);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (digit && index === 5) {
      setTimeout(() => formRef.current?.requestSubmit(), 50);
    }
  };

  const ready = otp.join("").length === 6;

  return (
    <form ref={formRef} onSubmit={onSubmit} style={s.form}>
      <p style={s.subheading}>
        We sent a 6-digit code to{" "}
        <strong style={{ color: "#e8e8e8" }}>{email}</strong>. Check your email
        on your phone or another device and enter the code below.
      </p>

      <div style={s.otpRow}>
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => { inputRefs.current[index] = el; }}
            className="dp-otp-input"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={digit}
            autoFocus={index === 0}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
          />
        ))}
      </div>

      <button
        type="submit"
        className="dp-btn dp-btn-primary"
        disabled={loading || !ready}
        style={{ opacity: loading || !ready ? 0.5 : 1, cursor: loading || !ready ? "not-allowed" : "pointer" }}
      >
        {loading ? "Verifying…" : "Sign In"}
      </button>

      <div style={s.otpFooter}>
        <span
          className="dp-link"
          style={{ opacity: resendCooldown > 0 ? 0.4 : 1, cursor: resendCooldown > 0 ? "default" : "pointer" }}
          onClick={resendCooldown > 0 ? undefined : onResend}
        >
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
        </span>
        <span className="dp-link" onClick={onBack}>
          Change email
        </span>
      </div>
    </form>
  );
});

// ── Identity Verified transition ─────────────────────────────────
const VERIFIED_TEXT = "IDENTITY VERIFIED";

const IdentityVerified = memo(({ name }: { name: string }) => {
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    // Start after the ring pops in (~300ms delay, then one char every 60ms)
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        setVisibleCount((c) => {
          if (c >= VERIFIED_TEXT.length) {
            clearInterval(interval);
            return c;
          }
          return c + 1;
        });
      }, 60);
      return () => clearInterval(interval);
    }, 350);
    return () => clearTimeout(timeout);
  }, []);

  const done = visibleCount >= VERIFIED_TEXT.length;

  return (
    <div style={s.verifiedWrap}>
      <div style={s.shieldRing}>
        <svg viewBox="0 0 36 36" style={s.checkmark as React.CSSProperties} className="dp-checkmark">
          <polyline points="7,19 15,27 29,11" />
        </svg>
      </div>

      <div style={s.verifiedBadge}>
        <span style={s.verifiedDot} className="dp-verified-dot" />
        <span style={{ letterSpacing: "1.5px" }}>
          {VERIFIED_TEXT.slice(0, visibleCount)}
          {!done && <span className="dp-cursor">|</span>}
        </span>
      </div>

      <div style={{ textAlign: "center", opacity: done ? 1 : 0, transition: "opacity 0.4s ease 0.1s" }}>
        <h2 style={s.welcomeHeading}>{name ? `Welcome, ${name}` : "Welcome"}</h2>
        <p style={s.welcomeSub}>Taking you to your dashboard…</p>
      </div>

      <div style={{ ...s.barWrap, opacity: done ? 1 : 0, transition: "opacity 0.4s ease 0.1s" }}>
        <div style={s.barTrack}>
          <div style={{ ...s.barFill, animationPlayState: done ? "running" : "paused" }} className="dp-bar-fill" />
        </div>
        <div style={s.barLabel}>
          <span style={s.barLabelText}>Redirecting</span>
        </div>
      </div>
    </div>
  );
});

// ── Logged-in card ───────────────────────────────────────────────
const LoggedInStatus = memo(({
  user,
  onContinue,
  onSignOut,
}: {
  user: User;
  onContinue: () => void;
  onSignOut: () => void;
}) => (
  <div style={{ ...s.form, alignItems: "center" }}>
    <div style={s.statusBadge}>
      <span style={s.statusDot} />
      Signed in
    </div>
    <div style={s.infoCard}>
      <div style={s.infoRow}>
        <span style={s.infoLabel}>Email</span>
        <span style={s.infoValue}>{user.email}</span>
      </div>
    </div>
    <button className="dp-btn dp-btn-primary" style={{ width: "100%" }} onClick={onContinue}>
      Continue
    </button>
    <button
      className="dp-btn dp-btn-ghost"
      style={{ width: "100%", borderColor: "rgba(255,80,80,0.3)", color: "#f87171" }}
      onClick={onSignOut}
    >
      Sign Out
    </button>
  </div>
));

// ── Main component ───────────────────────────────────────────────
const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const redirectUrl = new URLSearchParams(location.search).get("redirect") || "/";

  const [view, setView] = useState<"email" | "otp" | "verified" | "loggedIn">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [verifiedName, setVerifiedName] = useState<string>("");

  // Clear body styles set by the main app
  useEffect(() => {
    document.body.style.background = "";
    document.body.style.backgroundColor = "#0a0a0a";
    document.body.style.backgroundImage = "none";
    document.body.style.padding = "0";
    document.body.style.margin = "0";
  }, []);

  // Resend cooldown tick
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Auth state listener — skip straight to loggedIn if already signed in on mount
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        // Only jump to loggedIn if we're not mid-verification flow
        setView((prev) => (prev === "verified" ? "verified" : "loggedIn"));
      } else {
        setUser(null);
        setView((prev) => (prev === "verified" ? "verified" : "email"));
      }
    });
    return () => unsub();
  }, []);

  // Auto-redirect after verified animation (2.3s matches the bar fill)
  useEffect(() => {
    if (view !== "verified") return;
    const t = setTimeout(() => navigate(redirectUrl), 3800);
    return () => clearTimeout(t);
  }, [view, navigate, redirectUrl]);

  // ── Handlers ─────────────────────────────────────────────────
  const handleEmailChange = useCallback((v: string) => setEmail(v), []);

  const handleOtpChange = useCallback((index: number, value: string) => {
    setOtp((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const callSendOtp = useCallback(async () => {
    setLoading(true);
    setErrors([]);
    try {
      const fns = getFunctions(app, "europe-west2");
      const sendOtp = httpsCallable(fns, "sendOtpDineCentral");
      await sendOtp({ email: email.trim().toLowerCase() });
      setOtp(Array(6).fill(""));
      setView("otp");
      setResendCooldown(30);
    } catch (err: any) {
      setErrors([err?.message || "Failed to send code. Please try again."]);
    } finally {
      setLoading(false);
    }
  }, [email]);

  const handleEmailSubmit = useCallback(
    (e: React.FormEvent) => { e.preventDefault(); callSendOtp(); },
    [callSendOtp]
  );

  const handleResend = useCallback(() => callSendOtp(), [callSendOtp]);

  const handleBack = useCallback(() => {
    setView("email");
    setErrors([]);
    setOtp(Array(6).fill(""));
  }, []);

  const handleOtpSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);
    try {
      const fns = getFunctions(app, "europe-west2");
      const verifyOtp = httpsCallable(fns, "verifyOtpDineCentral");
      const result = await verifyOtp({
        email: email.trim().toLowerCase(),
        code: otp.join(""),
      });
      const { customToken } = result.data as { customToken: string };
      const auth = getAuth();
      await signInWithCustomToken(auth, customToken);

      // Grab the name claim before showing the transition
      const tokenResult = await auth.currentUser!.getIdTokenResult();
      const name = (tokenResult.claims.name as string | undefined) ?? "";
      setVerifiedName(name);
      setView("verified");
    } catch (err: any) {
      setErrors([err?.message || "Invalid code. Please try again."]);
      setLoading(false);
    }
  }, [email, otp]);

  const handleContinue = useCallback(() => navigate(redirectUrl), [navigate, redirectUrl]);

  const handleSignOut = useCallback(async () => {
    await getAuth().signOut();
    setView("email");
    setUser(null);
  }, []);

  // ── Render ───────────────────────────────────────────────────
  return (
    <>
      <style>{css}</style>
      <div style={s.page}>
        <div style={s.blobA} />
        <div style={s.blobB} />

        <div style={s.card} className="dp-card">
          {/* Logo */}
          <div style={s.logoRow}>
            <span className="dp-logo" style={{ fontWeight: 800, color: "white", fontSize: "26px", letterSpacing: "-0.5px" }}>
              Dine<span style={{ color: "royalblue" }}>Platform</span>
            </span>
          </div>

          {/* Heading */}
          {view === "email" && (
            <>
              <h1 style={s.heading} className="dp-heading">Welcome back</h1>
              <p style={s.subheading} className="dp-subheading">Enter your email to receive a sign-in code.</p>
            </>
          )}
          {view === "otp" && (
            <h1 style={s.heading} className="dp-heading">Enter your code</h1>
          )}

          {/* Views */}
          {view === "email" && (
            <EmailForm
              email={email}
              onEmailChange={handleEmailChange}
              onSubmit={handleEmailSubmit}
              loading={loading}
            />
          )}
          {view === "otp" && (
            <OtpForm
              email={email}
              otp={otp}
              onOtpChange={handleOtpChange}
              onSubmit={handleOtpSubmit}
              onBack={handleBack}
              onResend={handleResend}
              loading={loading}
              resendCooldown={resendCooldown}
            />
          )}
          {view === "verified" && (
            <IdentityVerified name={verifiedName} />
          )}
          {view === "loggedIn" && user && (
            <LoggedInStatus
              user={user}
              onContinue={handleContinue}
              onSignOut={handleSignOut}
            />
          )}

          {/* Errors */}
          {errors.map((err, i) => (
            <p key={i} style={s.errorText} className="dp-error-text">{err}</p>
          ))}
        </div>
      </div>
    </>
  );
};

/* ─── Static styles ──────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  page: {
    height: "100vh",
    width: "100vw",
    backgroundColor: "#0a0a0a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    position: "fixed",
    top: 0,
    left: 0,
    overflow: "hidden",
    fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
    boxSizing: "border-box",
  },
  blobA: {
    position: "absolute",
    top: "-120px",
    right: "-80px",
    width: "480px",
    height: "480px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(65,105,225,0.12) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  blobB: {
    position: "absolute",
    bottom: "-140px",
    left: "-100px",
    width: "520px",
    height: "520px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(65,105,225,0.07) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: "400px",
    backgroundColor: "#141414",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: "20px",
    padding: "40px 36px 32px",
    boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  logoRow: {
    marginBottom: "28px",
    textAlign: "center",
  },
  heading: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#ffffff",
    margin: "0 0 6px 0",
    letterSpacing: "-0.5px",
  },
  subheading: {
    fontSize: "15px",
    color: "#b0b0b0",
    margin: "0 0 24px 0",
    lineHeight: "1.5",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "14px",
    color: "#d0d0d0",
    fontWeight: 500,
  },
  otpRow: {
    display: "flex",
    gap: "8px",
    justifyContent: "center",
  },
  otpFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  errorText: {
    color: "#ff6b6b",
    fontSize: "14px",
    marginTop: "12px",
    textAlign: "center",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "rgba(34,197,94,0.1)",
    border: "1px solid rgba(34,197,94,0.25)",
    color: "#4ade80",
    fontSize: "13px",
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: "999px",
    marginBottom: "4px",
  },
  statusDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: "#22c55e",
    flexShrink: 0,
  },
  infoCard: {
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.07)",
    overflow: "hidden",
    width: "100%",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    gap: "12px",
  },
  infoLabel: {
    fontSize: "13px",
    color: "#888",
    fontWeight: 500,
    flexShrink: 0,
  },
  infoValue: {
    fontSize: "13px",
    color: "#ccc",
    fontWeight: 500,
    textAlign: "right",
    wordBreak: "break-all",
  },
  // ── Verified screen ──
  verifiedWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
    width: "100%",
    paddingTop: "8px",
    paddingBottom: "8px",
    animation: "dp-fadeUp 0.4s ease both",
  } as React.CSSProperties,
  shieldRing: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    background: "rgba(34,197,94,0.08)",
    border: "1.5px solid rgba(34,197,94,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "dp-ringPop 0.45s cubic-bezier(0.34,1.56,0.64,1) both",
  } as React.CSSProperties,
  checkmark: {
    width: "36px",
    height: "36px",
  },
  verifiedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(34,197,94,0.1)",
    border: "1px solid rgba(34,197,94,0.25)",
    color: "#4ade80",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    padding: "5px 14px",
    borderRadius: "999px",
    animation: "dp-fadeUp 0.4s ease 0.55s both",
    opacity: 0,
  } as React.CSSProperties,
  verifiedDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#22c55e",
    animation: "dp-pulse 1.5s ease-in-out infinite",
  } as React.CSSProperties,
  welcomeHeading: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#ffffff",
    letterSpacing: "-0.4px",
    margin: "0 0 4px 0",
    animation: "dp-fadeUp 0.4s ease 0.65s both",
    opacity: 0,
  } as React.CSSProperties,
  welcomeSub: {
    fontSize: "14px",
    color: "#888",
    margin: 0,
    animation: "dp-fadeUp 0.4s ease 0.7s both",
    opacity: 0,
  } as React.CSSProperties,
  barWrap: {
    width: "100%",
    animation: "dp-fadeUp 0.4s ease 0.75s both",
    opacity: 0,
  } as React.CSSProperties,
  barTrack: {
    width: "100%",
    height: "3px",
    background: "rgba(255,255,255,0.06)",
    borderRadius: "99px",
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    background: "linear-gradient(90deg, #4169E1, #22c55e)",
    borderRadius: "99px",
    animation: "dp-barFill 2s ease 0.9s forwards",
    width: "0%",
  } as React.CSSProperties,
  barLabel: {
    marginTop: "8px",
  },
  barLabelText: {
    fontSize: "12px",
    color: "#555",
  },
};

/* ─── CSS ────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');

  @keyframes dp-fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes dp-ringPop {
    from { transform: scale(0.5); opacity: 0; }
    to   { transform: scale(1);   opacity: 1; }
  }
  @keyframes dp-pulse {
    0%,100% { opacity: 1; } 50% { opacity: 0.3; }
  }
  @keyframes dp-drawCheck {
    to { stroke-dashoffset: 0; }
  }
  @keyframes dp-barFill {
    to { width: 100%; }
  }

  @keyframes dp-blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0; }
  }
  .dp-cursor {
    animation: dp-blink 0.7s step-end infinite;
    color: #4ade80;
    font-weight: 400;
    margin-left: 1px;
  }

  .dp-checkmark {
    stroke: #22c55e;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
    stroke-dasharray: 60;
    stroke-dashoffset: 60;
    animation: dp-drawCheck 0.5s ease 0.3s forwards;
  }

  .dp-verified-dot   { animation: dp-pulse 1.5s ease-in-out infinite !important; }
  .dp-verified-welcome h2 { animation: dp-fadeUp 0.4s ease 0.65s both !important; }
  .dp-verified-welcome p  { animation: dp-fadeUp 0.4s ease 0.70s both !important; }
  .dp-bar-wrap            { animation: dp-fadeUp 0.4s ease 0.75s both !important; }

  .dp-btn {
    padding: 13px 20px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    box-sizing: border-box;
  }

  .dp-btn:disabled { cursor: not-allowed; }

  .dp-btn-primary {
    background: #4169E1;
    color: #ffffff;
    box-shadow: 0 4px 20px rgba(65,105,225,0.3);
    width: 100%;
  }

  .dp-btn-primary:hover:not(:disabled) {
    background: #5578e8;
    transform: translateY(-1px);
    box-shadow: 0 6px 24px rgba(65,105,225,0.4);
  }

  .dp-btn-ghost {
    background: transparent;
    color: #999;
    border: 1px solid rgba(255,255,255,0.08);
    font-weight: 500;
  }

  .dp-btn-ghost:hover:not(:disabled) {
    color: #bbb;
    background: rgba(255,255,255,0.03);
  }

  .dp-input {
    background: #1a1a1a;
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 9px;
    padding: 12px 14px;
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    color: #e8e8e8;
    width: 100%;
    box-sizing: border-box;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .dp-input::placeholder { color: #555; }

  .dp-input:focus {
    border-color: rgba(65,105,225,0.5);
    box-shadow: 0 0 0 3px rgba(65,105,225,0.08);
  }

  .dp-otp-input {
    width: 44px;
    height: 52px;
    font-size: 22px;
    font-weight: 700;
    text-align: center;
    border-radius: 9px;
    border: 1px solid rgba(255,255,255,0.09);
    background: #1a1a1a;
    color: #e8e8e8;
    outline: none;
    font-family: 'DM Sans', sans-serif;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .dp-otp-input:focus {
    border-color: rgba(65,105,225,0.5);
    box-shadow: 0 0 0 3px rgba(65,105,225,0.08);
  }

  .dp-link {
    color: royalblue;
    cursor: pointer;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    transition: opacity 0.15s ease;
  }

  .dp-link:hover { opacity: 0.75; }

  @media (min-width: 768px) {
    .dp-card {
      max-width: 480px !important;
      padding: 48px 43px 38px !important;
    }
    .dp-logo { font-size: 31px !important; }
    .dp-heading { font-size: 29px !important; }
    .dp-subheading { font-size: 18px !important; }
    .dp-btn {
      font-size: 17px !important;
      padding: 15px 24px !important;
    }
    .dp-input {
      font-size: 17px !important;
      padding: 14px 17px !important;
    }
    .dp-otp-input {
      width: 53px !important;
      height: 62px !important;
      font-size: 26px !important;
    }
    .dp-link { font-size: 15px !important; }
    .dp-error-text { font-size: 15px !important; }
  }
`;

export default Login;